import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {skazkiAudio, useSkazkiAudio} from '../../utils/skazkiAudio';
import {formatDuration, SKAZKI_GENRES} from '../../config/skazkiConstants';
import {Asterisk} from './SkazkiIllustration';
import SkazkiTaleBookText from './SkazkiTaleBookText';

export default function SkazkiPlayer({
                                         tale,
                                         quietMode,
                                         skazkiCredits = 0,
                                         unlockedOptimistic = false,
                                         subscriptionActive = false,
                                         subscribing = false,
                                         talesPerMonth = 6,
                                         planPrice = 490,
                                         onCreateAnother,
                                         onBack,
                                         onSubscribe,
                                         onUnlock,
                                     }) {
    const audioState = useSkazkiAudio();
    const [shareCopied, setShareCopied] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [listenedReported, setListenedReported] = useState(false);
    const [audioProgress, setAudioProgress] = useState(0);
    const menuRef = useRef(null);
    const audioCleanupRef = useRef(null);

    // Derived flags (null-safe). Some effects below depend on these, so they're
    // computed before the early `!tale` return to keep all hooks unconditional.
    const previewLocked = !!tale && !tale.paid && skazkiCredits <= 0 && !unlockedOptimistic;
    // Full TTS still cooking: user has access (paid / activated) but the backend
    // hasn't filled audio_url yet. The preview clip keeps playing in the
    // meantime; we surface a progress bar so the wait feels intentional.
    const audioGenerating = !!tale && !previewLocked && !tale.audio_url && tale.status !== 'failed';

    useEffect(() => {
        if (!tale) return;
        skazkiAudio.loadTale(tale);
    }, [tale]);

    const audioMountRef = useCallback((node) => {
        if (audioCleanupRef.current) {
            audioCleanupRef.current();
            audioCleanupRef.current = null;
        }
        if (node) {
            audioCleanupRef.current = skazkiAudio.mountAudioElement(node);
        }
    }, []);

    useEffect(() => {
        if (!tale?.duration_sec || listenedReported) return;
        if (audioState.currentSec / tale.duration_sec >= 0.9) {
            trackEvent('skazki_listen_complete');
            setListenedReported(true);
        }
    }, [audioState.currentSec, tale, listenedReported]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onClick = (e) => {
            if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
        };
        window.addEventListener('click', onClick);
        return () => window.removeEventListener('click', onClick);
    }, [menuOpen]);

    useEffect(() => {
        if (!audioGenerating) {
            setAudioProgress(0);
            return undefined;
        }
        const start = Date.now();
        const t = setInterval(() => {
            const elapsed = (Date.now() - start) / 1000;
            setAudioProgress(Math.min(92, Math.round((elapsed / 30) * 92)));
        }, 250);
        return () => clearInterval(t);
    }, [audioGenerating, tale?.id]);

    const taleText = useMemo(() => {
        if (!tale) return '';
        if (tale.text) return tale.text;
        if (tale.chapters?.length) return tale.chapters.map(c => c.text).join('\n\n');
        return '';
    }, [tale]);

    if (!tale) return null;

    const genreName = SKAZKI_GENRES.find(g => g.id === tale.genre)?.name?.toLowerCase() || '';
    const durationSec = tale.duration_sec || audioState.durationSec || 0;
    const interestChips = (tale.interests || []).slice(0, 4);
    const canDownload = !!tale.audio_url;

    const handleShare = async () => {
        const url = `${window.location.origin}/skazki/s/${tale.id}`;
        trackEvent('skazki_share');
        if (navigator.share) {
            try {
                await navigator.share({title: tale.title, url});
                return;
            } catch (err) {
                if (err.name === 'AbortError') return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        } catch {
            showError('Не удалось скопировать ссылку');
        }
    };

    const handleDownload = () => {
        if (!tale.audio_url) {
            showError('Сказка ещё не готова для скачивания');
            return;
        }
        trackEvent('skazki_download');
        const a = document.createElement('a');
        a.href = tale.audio_url;
        a.download = `${tale.title}.mp3`;
        a.click();
    };

    // ============ LOCKED: single merged paywall view ============
    if (previewLocked) {
        return (
            <LockedPaywall
                tale={tale}
                quietMode={quietMode}
                planPrice={planPrice}
                talesPerMonth={talesPerMonth}
                subscribing={subscribing}
                subscriptionActive={subscriptionActive}
                onSubscribe={onSubscribe}
                onBack={onBack}
            />
        );
    }

    // ============ UNLOCKED player ============
    return (
        <div className={`${quietMode ? 'skazki-quiet' : ''}`}>
            <div
                className="max-w-[1200px] mx-auto my-4 md:my-8 bg-white md:rounded-[20px] md:border md:border-[#F6F6F6] md:shadow-[0_8px_32px_rgba(14,14,14,0.06)] overflow-hidden">

                {/* Mobile header */}
                <div className="flex items-center justify-between px-5 py-3 md:hidden">
                    <span className="font-bold"><Asterisk className="text-[#FF6464]"/> готово</span>
                    <button
                        onClick={() => setMenuOpen(v => !v)}
                        aria-label="Меню"
                        className="text-lg text-[#0E0E0E] relative"
                    >⋯
                        {menuOpen && (
                            <div ref={menuRef} role="menu"
                                 className="absolute top-full right-0 mt-1 bg-white border border-[#F6F6F6] rounded-[12px] shadow-lg py-1 z-10 min-w-[180px] text-left">
                                <MenuBtn
                                    onClick={handleShare}>{shareCopied ? 'Ссылка скопирована' : 'Поделиться'}</MenuBtn>
                            </div>
                        )}
                    </button>
                </div>

                {/* Desktop grid */}
                <div className="md:grid md:grid-cols-[460px_1fr] md:min-h-[640px]">

                    {/* Main content */}
                    <div className="px-5 py-5 md:px-10 md:py-14 flex flex-col min-h-0">

                        {/* Desktop header eyebrow */}
                        <div className="hidden md:flex items-center justify-between mb-2">
                            <div
                                className="text-[13px] font-semibold tracking-[0.08em] uppercase text-[#FF6464]">
                                <Asterisk/> Сказка готова
                            </div>
                        </div>

                        <div className="text-center md:text-left">
                            <h1 className="font-bold text-[#0E0E0E] tracking-[-0.02em] leading-[1.05] text-[26px] md:text-[48px]">
                                {tale.title}
                            </h1>
                            <div className="text-[12px] md:text-[14px] text-[#3D3D3D]/80 mt-1">
                                для {tale.child_name}, {tale.age} лет{genreName ? ` · ${genreName}` : ''} · {formatDuration(durationSec)}
                            </div>
                        </div>

                        {interestChips.length > 0 && (
                            <div className="hidden md:flex flex-wrap gap-[6px] mt-4">
                                {interestChips.map((i, idx) => {
                                    const label = typeof i === 'string' ? i : i.label;
                                    return (
                                        <span key={idx}
                                              className="px-[10px] py-[3px] rounded-full text-[12px] bg-[rgba(255,100,100,0.1)] text-[#3D3D3D]">{label}</span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Native audio player — hidden while the full TTS is still cooking,
                            so users don't see the stale preview clip after unlocking */}
                        {!audioGenerating && <div className="mt-5 md:mt-8" ref={audioMountRef}/>}

                        {audioGenerating && (
                            <div
                                className="sk-card-elevated mt-5 md:mt-8 flex items-center gap-3 px-4 py-[10px] rounded-full">
                                <span className="text-lg">🎙</span>
                                <span
                                    className="text-[12px] md:text-[13px] text-[#3D3D3D]/80 whitespace-nowrap">
                                    Озвучиваем полную сказку…
                                </span>
                                <div
                                    className="flex-1 h-[6px] rounded-full bg-[#F6F6F6] overflow-hidden relative">
                                    <div
                                        className="absolute inset-y-0 left-0 bg-[#FF6464] rounded-full transition-all"
                                        style={{width: `${audioProgress}%`}}/>
                                </div>
                                <span
                                    className="font-bold text-[#FF6464] text-sm tabular-nums">{audioProgress}%</span>
                            </div>
                        )}

                        {skazkiCredits > 0 && !tale.paid && onUnlock && (
                            <div
                                className="mt-6 md:mt-8 rounded-[14px] border border-[#FF6464]/30 bg-[rgba(255,100,100,0.08)] p-4 md:p-5">
                                <p className="text-[14px] md:text-[15px] text-[#0E0E0E] leading-[1.4] mb-3">
                                    Это было превью. Нажмите, чтобы сгенерировать полную аудиосказку — с вашего баланса
                                    спишется 1 сказка.
                                </p>
                                <button
                                    onClick={onUnlock}
                                    className="sk-btn sk-btn--gold w-full h-[48px] md:h-[52px] text-[15px] md:text-[16px]"
                                >
                                    Сгенерировать полную сказку
                                </button>
                            </div>
                        )}

                        {/* Desktop: action buttons */}
                        <div className="hidden md:flex gap-3 mt-auto pt-12">
                            {canDownload && (
                                <button onClick={handleDownload}
                                        className="sk-btn sk-btn--ghost flex-1 h-[52px] text-[16px]">↓ Скачать MP3
                                </button>
                            )}
                            <button onClick={onCreateAnother}
                                    className="sk-btn sk-btn--gold flex-1 h-[52px] text-[16px]">+ Ещё сказку
                            </button>
                        </div>

                        {/* Mobile: primary action buttons */}
                        <div className="md:hidden flex gap-2 mt-6">
                            {canDownload && (
                                <button onClick={handleDownload}
                                        className="sk-btn sk-btn--ghost flex-1 h-[48px] text-[15px]">↓ скачать
                                </button>
                            )}
                            <button onClick={onCreateAnother}
                                    className="sk-btn sk-btn--gold flex-1 h-[48px] text-[15px]">+ ещё сказку
                            </button>
                        </div>

                        {/* Desktop: share row */}
                        <div className="hidden md:flex gap-4 mt-4 text-sm">
                            <button onClick={handleShare}
                                    className="text-[#3D3D3D]/80 hover:text-[#0E0E0E] transition">
                                {shareCopied ? 'Ссылка скопирована' : '🔗 Поделиться'}
                            </button>
                        </div>
                    </div>

                    {/* Right column: tale text */}
                    <div
                        className="sk-panel-deep px-5 pt-2 pb-6 md:px-14 md:py-12 flex flex-col min-h-0">
                        <SkazkiTaleBookText text={taleText} title={tale.title}/>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MenuBtn({onClick, children}) {
    return (
        <button
            role="menuitem"
            onClick={onClick}
            className="w-full text-left px-3 py-2 text-sm text-[#0E0E0E] hover:bg-[#F6F6F6] transition"
        >{children}</button>
    );
}

// ============================================================
// Locked preview → hard paywall
// Two columns on desktop (story + audio + contents | offer),
// a single stacked column on mobile.
// ============================================================

// Sample upcoming tales shown in "Что входит в подписку" (marketing teasers).
const UPCOMING_TALES = [
    {emoji: '🌙', title: 'Поможет уснуть без уговоров', desc: 'Спокойная сказка для лёгкого засыпания'},
    {emoji: '🦁', title: 'Научит смелости', desc: 'История, которая поможет поверить в себя'},
    {emoji: '❤️', title: 'Научит доброте', desc: 'Сказка о заботе и дружбе'},
    {emoji: '👂', title: 'Научит слушать родителей', desc: 'Поможет понять и услышать важное'},
    {emoji: '🚀', title: 'Новое приключение', desc: 'Ребёнок снова станет главным героем'},
];

function LockedPaywall({
                           tale,
                           quietMode,
                           planPrice = 490,
                           talesPerMonth = 6,
                           subscribing = false,
                           subscriptionActive = false,
                           onSubscribe,
                           onBack,
                       }) {
    const price = planPrice > 0 ? planPrice : 490;
    const tales = talesPerMonth > 0 ? talesPerMonth : 6;
    // "this one now + N more each month"
    const moreCount = Math.max(1, tales - 1);
    const fullDuration = formatMinutes(tale.duration_sec);

    return (
        <div className={quietMode ? 'skazki-quiet' : ''}>
            <div className="max-w-[1180px] mx-auto my-4 md:my-9 px-4 sm:px-6 overflow-x-hidden">
                <div className="md:grid md:grid-cols-[1fr_minmax(360px,420px)] md:gap-x-8 md:gap-y-9 md:items-start">

                    {/* ── Header + audio (left col, row 1 · mobile first) ── */}
                    <div className="md:col-start-1 md:row-start-1">
                        <div className="inline-flex items-center gap-2.5 text-[#FF6464] font-extrabold uppercase tracking-[0.01em] leading-none text-[28px] md:text-[40px]">
                            <Asterisk/> Сказка готова
                        </div>
                        <h1 className="mt-3 font-bold text-[#0E0E0E] tracking-[-0.01em] leading-[1.15] text-[19px] md:text-[24px]">
                            {tale.title}
                        </h1>
                        <div className="text-[12px] md:text-[13px] text-[#9A9AA2] mt-1.5">
                            для {tale.child_name}, {tale.age} лет{fullDuration ? ` · ${fullDuration}` : ''}
                        </div>

                        <div className="mt-4 md:mt-5">
                            <AudioPreviewWave tale={tale}/>
                        </div>

                        {/* Contents grid lives in row 2 on desktop; on mobile it
                            renders right after the offer (see below) */}
                        <div className="hidden md:block md:mt-8">
                            <SubscriptionContents tale={tale} moreCount={moreCount}/>
                        </div>
                    </div>

                    {/* ── Offer card (right col, spans rows · mobile second) ── */}
                    <div className="mt-6 md:mt-0 md:col-start-2 md:row-start-1 md:row-span-2 md:self-start">
                        <OfferCard
                            price={price}
                            tales={tales}
                            moreCount={moreCount}
                            subscribing={subscribing}
                            subscriptionActive={subscriptionActive}
                            onSubscribe={onSubscribe}
                        />
                    </div>

                    {/* Contents grid for mobile only (after the offer) */}
                    <div className="mt-8 md:hidden">
                        <SubscriptionContents tale={tale} moreCount={moreCount}/>
                    </div>
                </div>

                {/* ── Back ── */}
                <div className="mt-7">
                    <button onClick={onBack}
                            className="sk-btn sk-btn--ghost h-[50px] px-6 text-[15px] inline-flex items-center justify-center gap-2">
                        <BackGlyph/> Назад
                    </button>
                </div>
            </div>
        </div>
    );
}

// Bigger audio preview: cover + a real play/pause control over a red waveform.
// While the clip is still being recorded it shows a spinner + "Записываем…".
function AudioPreviewWave({tale}) {
    const {isPlaying, currentSec, durationSec: clipDur} = useSkazkiAudio();
    const hasAudio = !!(tale.preview_url || tale.audio_url);
    const failed = !hasAudio && tale.status === 'failed';
    const preparing = !hasAudio && !failed;
    const total = clipDur || 0;
    const progress = total > 0 ? Math.min(1, currentSec / total) : 0;

    const BARS = 48;
    const bars = useMemo(
        () => Array.from({length: BARS}, (_, i) => {
            const n = Math.abs(Math.sin(i * 12.9898) * 43758.5453);
            return 0.16 + 0.84 * (n - Math.floor(n));
        }),
        [],
    );

    const toggle = () => {
        if (!hasAudio) return;
        trackEvent('skazki_preview_play');
        skazkiAudio.toggle();
    };

    const label = preparing
        ? 'Записываем озвучку…'
        : failed ? 'Не удалось записать озвучку' : 'Прослушайте начало сказки';
    const timeLabel = formatDuration((isPlaying || currentSec > 0) ? currentSec : total);

    return (
        <div className="rounded-[16px] border border-[#EFEAE6] bg-white p-3 md:p-3.5 flex items-center gap-3 md:gap-4 shadow-[0_4px_20px_rgba(14,14,14,0.04)]">
            <div className="flex-shrink-0 w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-[12px] bg-[#FFF1F1] flex items-center justify-center">
                <span className="text-[28px] md:text-[32px]" aria-hidden="true">📖</span>
            </div>

            <div className="flex-1 min-w-0">
                <div className="text-[12px] md:text-[13px] text-[#3D3D3D]/80 mb-2 inline-flex items-center gap-1.5">
                    <span aria-hidden="true">🎧</span> {label}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={toggle}
                        disabled={!hasAudio}
                        aria-label={preparing ? 'Записываем озвучку' : isPlaying ? 'Пауза' : 'Слушать превью'}
                        className="flex-shrink-0 w-[44px] h-[44px] md:w-[50px] md:h-[50px] rounded-full bg-[#FF6464] text-white flex items-center justify-center shadow-[0_4px_14px_rgba(255,100,100,0.4)] hover:scale-[1.04] active:scale-95 transition disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-default"
                    >
                        {preparing
                            ? <span className="inline-block w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin"/>
                            : isPlaying ? <PauseGlyph/> : <PlayGlyph/>}
                    </button>

                    <div className={`flex-1 flex items-center gap-[2px] h-[34px] md:h-[40px] ${preparing ? 'animate-pulse' : ''}`}>
                        {bars.map((h, i) => {
                            const played = hasAudio && (i + 0.5) / BARS <= progress;
                            return (
                                <span
                                    key={i}
                                    style={{height: `${Math.round(h * 100)}%`}}
                                    className={`flex-1 rounded-full ${played ? 'bg-[#FF6464]' : preparing ? 'bg-[#0E0E0E]/[0.12]' : 'bg-[#FF6464]/45'}`}
                                />
                            );
                        })}
                    </div>

                    {!preparing && (
                        <span className="flex-shrink-0 text-[12px] md:text-[13px] text-[#9A9AA2] tabular-nums w-[34px] text-right">
                            {timeLabel}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function SubscriptionContents({tale, moreCount}) {
    const samples = UPCOMING_TALES.slice(0, moreCount);
    return (
        <div>
            <h3 className="font-bold text-[#0E0E0E] text-[15px] md:text-[16px] mb-3">Что входит в подписку</h3>
            <div className="flex gap-2.5 overflow-x-auto pb-1 md:grid md:grid-cols-6 md:gap-2.5 md:overflow-visible md:pb-0">
                <ContentCard done emoji="📖" title={tale.title}/>
                {samples.map((s, i) => (
                    <ContentCard key={i} emoji={s.emoji} title={s.title} desc={s.desc}/>
                ))}
            </div>
        </div>
    );
}

function ContentCard({done = false, emoji, title, desc}) {
    return (
        <div className="flex-shrink-0 w-[100px] md:w-auto">
            <div className={`relative rounded-[14px] aspect-[3/4] flex items-center justify-center ${done ? 'ring-[1.5px] ring-[#FF6464] ring-inset bg-[#FFF1F1]' : 'bg-[#F4F2EF] border border-[#ECE8E4]'}`}>
                <span className="text-[30px] md:text-[34px]" aria-hidden="true">{emoji}</span>
                <span className={`absolute top-1.5 right-1.5 w-[22px] h-[22px] rounded-full flex items-center justify-center ${done ? 'bg-[#FF6464] text-white' : 'bg-black/70 text-white'}`}>
                    {done ? <CheckGlyph/> : <LockGlyph/>}
                </span>
            </div>
            <div className={`mt-2 text-[11px] font-bold leading-[1.2] ${done ? 'text-[#FF6464]' : 'text-[#0E0E0E]'}`}>
                {done ? 'Уже готово' : title}
            </div>
            <div className={`text-[10px] leading-[1.25] mt-0.5 ${done ? 'text-[#FF6464]/80' : 'text-[#9A9AA2]'}`}>
                {done ? title : desc}
            </div>
        </div>
    );
}

function OfferCard({price, tales, moreCount, subscribing, subscriptionActive, onSubscribe}) {
    const handleSubscribe = () => {
        if (subscribing) return;
        trackEvent('skazki_subscription_start_clicked');
        onSubscribe?.();
    };

    if (subscriptionActive) {
        return (
            <div className="rounded-[22px] border border-[#EFEAE6] bg-[#FBF8F5] p-6 text-center">
                <h2 className="font-bold text-[#0E0E0E] text-[20px] md:text-[22px] leading-[1.2]">
                    Сказки в этом месяце закончились
                </h2>
                <p className="mt-2 text-[13px] md:text-[14px] text-[#3D3D3D]/80 leading-[1.5]">
                    Лимит из {tales} {pluralTales(tales)} обновится в начале следующего периода.
                </p>
            </div>
        );
    }

    return (
        <div className="rounded-[22px] border-[1.5px] border-[#FF6464] bg-white p-6 md:p-7 shadow-[0_10px_40px_rgba(255,100,100,0.10)]">
            <h2 className="font-bold text-[#0E0E0E] text-[21px] md:text-[23px] leading-[1.22]">
                Получите эту сказку сразу
            </h2>
            <p className="font-bold text-[#FF6464] text-[21px] md:text-[23px] leading-[1.22]">
                и ещё {moreCount} новых {pluralTales(moreCount)} каждый месяц
            </p>

            <ul className="mt-5 flex flex-col gap-3">
                <CheckItem>Первая сказка уже готова</CheckItem>
                <CheckItem>Ещё {moreCount} новых {pluralTales(moreCount)} каждый месяц</CheckItem>
                <CheckItem>Профессиональная озвучка</CheckItem>
                <CheckItem>Новые приключения каждый месяц</CheckItem>
                <CheckItem>Отмена в один клик</CheckItem>
            </ul>

            <div className="mt-6 pt-5 border-t border-dashed border-[#E8E8E8]">
                <div className="flex items-baseline gap-2">
                    <span className="font-extrabold text-[#FF6464] text-[44px] md:text-[48px] leading-none tracking-[-0.02em]">
                        {price} ₽
                    </span>
                    <span className="text-[#9A9AA2] text-[15px]">/ месяц</span>
                </div>

                <button
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    className="sk-btn sk-btn--gold w-full h-[58px] text-[18px] mt-4 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                    {subscribing ? (
                        <>
                            <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>
                            Открываем оплату…
                        </>
                    ) : (
                        <>Получить сказку <Asterisk/></>
                    )}
                </button>

                <p className="mt-3 text-center text-[12px] text-[#9A9AA2]">
                    {price} ₽ в месяц. Отменить можно в любой момент.
                </p>
                <p className="mt-1 text-center text-[12px] text-[#9A9AA2]">
                    🔒 Безопасно. Ваши данные под защитой.
                </p>
            </div>

            <div className="mt-5 pt-4 border-t border-dashed border-[#F0F0F0] flex items-center justify-between gap-3 text-[11px] text-[#9A9AA2]">
                <div>
                    <span className="text-[#0E0E0E] font-medium">🔒 Безопасно</span>
                    <div>СБП · карты · ЮКасса</div>
                </div>
                <div className="text-right">
                    <span className="text-[#FF6464]">★ 4.9</span>
                    <div>3000+ отзывов</div>
                </div>
            </div>
        </div>
    );
}

function CheckItem({children}) {
    return (
        <li className="flex items-center gap-3">
            <span className="flex-shrink-0 w-[22px] h-[22px] rounded-full bg-[#FF6464] text-white flex items-center justify-center">
                <CheckGlyph/>
            </span>
            <span className="text-[14px] md:text-[15px] text-[#0E0E0E] leading-[1.3]">{children}</span>
        </li>
    );
}

function PlayGlyph() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
            <path d="M5 3.4v11.2a.6.6 0 0 0 .92.5l8.8-5.6a.6.6 0 0 0 0-1l-8.8-5.6A.6.6 0 0 0 5 3.4z"/>
        </svg>
    );
}

function PauseGlyph() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="3" y="2.5" width="3.5" height="11" rx="1.2"/>
            <rect x="9.5" y="2.5" width="3.5" height="11" rx="1.2"/>
        </svg>
    );
}

function CheckGlyph() {
    return (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2.5 7.4l3 3 6-6.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function LockGlyph() {
    return (
        <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="inline-block">
            <rect x="2.5" y="6" width="9" height="6.4" rx="1.6" fill="currentColor"/>
            <path d="M4.6 6V4.4a2.4 2.4 0 0 1 4.8 0V6" stroke="currentColor" strokeWidth="1.4"/>
        </svg>
    );
}

function BackGlyph() {
    return (
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 4l-5 5 5 5"/>
        </svg>
    );
}

function pluralTales(n) {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return 'сказка';
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'сказки';
    return 'сказок';
}

function formatMinutes(totalSec) {
    if (!totalSec) return null;
    const m = Math.max(1, Math.round(totalSec / 60));
    const m10 = m % 10;
    const m100 = m % 100;
    let word = 'минут';
    if (m10 === 1 && m100 !== 11) word = 'минута';
    else if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) word = 'минуты';
    return `${m} ${word}`;
}
